// src/utils/firestoreCache.js
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Local storage key used for product cache.
 */
const CACHE_KEY = "products";

/**
 * Maximum cache age in milliseconds (currently 5 minutes).
 */
const CACHE_MAX_AGE_MS = 1000 * 60 * 5;

/**
 * Checks if localStorage is available and writable in the current environment.
 * (Defensive – avoids runtime errors e.g. in older browsers or unusual setups.)
 */
const isLocalStorageAvailable = (() => {
  try {
    const testKey = "__codaco_cache_test__";
    window.localStorage.setItem(testKey, "1");
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
})();

/**
 * Saves data to localStorage cache under the given key.
 * If localStorage is not available, it silently does nothing.
 */
const saveCache = (key, data) => {
  if (!isLocalStorageAvailable) return;

  try {
    const payload = {
      data,
      timestamp: Date.now(),
    };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {
    // If quota is exceeded or JSON fails, we just skip caching.
    console.warn("[Cache] Failed to save cache:", error);
  }
};

/**
 * Attempts to load cached data from localStorage.
 * - Returns `null` if cache is missing, expired or invalid.
 * - Returns parsed `data` if the cache is still fresh.
 */
const loadCache = (key, maxAgeMs) => {
  if (!isLocalStorageAvailable) return null;

  try {
    const cachedRaw = window.localStorage.getItem(key);
    if (!cachedRaw) return null;

    const parsed = JSON.parse(cachedRaw);

    // Basic shape validation
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const { data, timestamp } = parsed;
    if (typeof timestamp !== "number") {
      return null;
    }

    const age = Date.now() - timestamp;
    if (age > maxAgeMs) {
      console.log("[Cache] Expired, fetching new data...");
      return null;
    }

    console.log("[Cache] Using cached data");
    return data;
  } catch (error) {
    console.warn("[Cache] Failed to read cache:", error);
    return null;
  }
};

/**
 * Fetches products from Firestore with a simple localStorage cache in front.
 *
 * Flow:
 * 1) Try to read valid cache.
 * 2) If cache exists and is fresh, return it.
 * 3) Otherwise, fetch products from Firestore, store them in cache and return.
 */
export const fetchProductsWithCache = async () => {
  // 1) Try cache first
  const cachedData = loadCache(CACHE_KEY, CACHE_MAX_AGE_MS);
  if (cachedData) {
    return cachedData;
  }

  // 2) Fallback: fetch from Firestore
  console.log("[Cache] Fetching from Firestore...");
  try {
    const querySnapshot = await getDocs(collection(db, "products"));

    const products = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // 3) Save to cache (best effort)
    saveCache(CACHE_KEY, products);
    console.log("[Cache] Saved new data to cache");

    return products;
  } catch (error) {
    console.error("[Cache] Failed to fetch products from Firestore:", error);
    // In case of error, return empty array instead of crashing the UI.
    return [];
  }
};
